/**
 * Copyright (c) Microsoft. All rights reserved.
 */
/* eslint-disable no-bitwise */
import { getTheme } from '@fluentui/react/lib/Styling';
const { palette } = getTheme();
export const accessibleEntityColorsWithWhiteBackground = [
    '#00758F',
    '#B34F90',
    '#4F6BED',
    '#6D5700',
    '#286EA8',
    '#A43FB1',
    '#CC3595',
    '#0E7878',
    '#8764B8',
    '#9C663F',
    '#00566A',
    '#AE4689',
    '#4661D5',
    '#3A2F00',
    '#125D9A',
    '#972AA5',
    '#AC2D7E',
    '#006666',
    '#7959A5',
    '#8E562E',
    '#002E39',
    '#982570',
    '#3B52B4',
    '#1F1900',
    '#004E8C',
    '#881798',
    '#7F215D',
    '#005C5C',
    '#664B8C',
    '#814E29',
    '#00191E',
    '#88105E',
    '#2C3C85',
    '#00457E',
    '#7C158A',
    '#441232',
    '#004E4E',
    '#4B3867',
    '#6D4123',
    '#77004D',
    '#172047',
    '#003B6B',
    '#691174',
    '#003939',
    '#281E37',
    '#50301A',
    '#5B003B',
    '#002B4F',
    '#4D0D56',
    '#001F1F',
    '#2B1A0E',
    '#43002C',
    '#00172A',
    '#29072E',
    '#240017'
];
export const generateEntityColorFromString = s => {
    const newStr = s ? s.toUpperCase() : s;
    let hash = 0;
    for (let i = 0; i < (newStr === null || newStr === void 0 ? void 0 : newStr.length); i++) {
        hash = newStr.charCodeAt(i) + ((hash << 5) - hash);
        hash &= hash;
    }
    hash =
        ((hash % accessibleEntityColorsWithWhiteBackground.length) + accessibleEntityColorsWithWhiteBackground.length) %
        accessibleEntityColorsWithWhiteBackground.length;
    return accessibleEntityColorsWithWhiteBackground[hash];
};
const theme = {
    ipEntityColor: '#00B24A',
    urlEntityColor: '#1CC2B2',
    eventEntityColor: '#BE0151',
    skillEntityColor: '#C39F74',
    emailEntityColor: '#7CC102',
    personEntityColor: '#018A39',
    productEntityColor: '#E41323',
    addressEntityColor: '#FABE14',
    locationEntityColor: '#F2610C',
    dateTimeEntityColor: '#029DD4',
    quantityEntityColor: '#829196',
    personTypeEntityColor: '#A80088',
    phoneNumberEntityColor: '#CF0076',
    organizationEntityColor: '#0166AC'
};
export const getEntityColor = entityCategory => {
    switch (entityCategory.replace(' ', '')) {
        case 'Person':
            return theme.personEntityColor;
        case 'PersonType':
            return theme.personTypeEntityColor;
        case 'Location':
            return theme.locationEntityColor;
        case 'Organization':
            return theme.organizationEntityColor;
        case 'Event':
            return theme.eventEntityColor;
        case 'Product':
            return theme.productEntityColor;
        case 'Skill':
            return theme.skillEntityColor;
        case 'Address':
            return theme.addressEntityColor;
        case 'PhoneNumber':
            return theme.phoneNumberEntityColor;
        case 'Email':
            return theme.emailEntityColor;
        case 'URL':
            return theme.urlEntityColor;
        case 'IP':
            return theme.ipEntityColor;
        case 'DateTime':
            return theme.dateTimeEntityColor;
        case 'Quantity':
            return theme.quantityEntityColor;
        case 'Rank':
            return palette.themeDark;
        default:
            return generateEntityColorFromString(entityCategory);
    }
};
const getEntityId = entity => {
    const namedEntity = entity;
    return `${namedEntity.category}${namedEntity.offset}${namedEntity.length}`;
};
const calculateRenderingLevel = (entity, entities) => {
    let renderingLevel = 1;
    entities.forEach(e => {
        if (getEntityId(e) !== getEntityId(entity) && e.offset >= entity.offset && e.offset + e.length <= entity.offset + entity.length) {
            renderingLevel++;
        }
    });
    return renderingLevel;
};
const getLabel = (entity, tokens, entities) => {
    var _a;
    const id = getEntityId(entity);
    const eTokens = (_a = tokens.find(t => t.id === id)) === null || _a === void 0 ? void 0 : _a.tokens;
    const renderingLevel = calculateRenderingLevel(entity, entities);
    return {
        kind: 'label',
        name: entity.category,
        level: renderingLevel - 1,
        endToken: eTokens === null || eTokens === void 0 ? void 0 : eTokens.endTokenIndex,
        startToken: eTokens === null || eTokens === void 0 ? void 0 : eTokens.startTokenIndex,
        color: getEntityColor(entity.category),
        id: `${entity.category}-line-${renderingLevel}-${eTokens === null || eTokens === void 0 ? void 0 : eTokens.startTokenIndex}-${
            eTokens === null || eTokens === void 0 ? void 0 : eTokens.endTokenIndex
        }`
    };
};
export const formatSentenceAnnotations = sentence => {
    if (!sentence.entities) {
        return [];
    }
    const tokens = sentence.entities.map(entity => ({
        id: getEntityId(entity),
        tokens: {
            startTokenIndex: entity.offset,
            endTokenIndex: entity.offset + entity.length - 1
        }
    }));
    return sentence.entities.map(e => getLabel(e, tokens, sentence.entities));
};
export const getReadableDuration = duration =>
    duration ? `${duration.minutes < 10 ? '0' : ''}${duration.minutes}:${duration.seconds < 10 ? '0' : ''}${duration.seconds}` : '';
